import {
  Box,
  FormHelperText,
  InputAdornment,
  OutlinedTextFieldProps,
  SxProps,
  TextField,
  Typography,
} from '@mui/material'
import { styled } from '@mui/system'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import he from 'he'
import React, { useEffect, useState } from 'react'

import { createYupSchema } from '../utils/yup'

export enum ValidationType {
  required = 'required',
  length = 'length',
  min = 'min',
  max = 'max',
  matches = 'matches',
  email = 'email',
  url = 'url',
  uuid = 'uuid',
  lowercase = 'lowercase',
  uppercase = 'uppercase',
}

interface IValidation {
  type: ValidationType
  params: (string | number | RegExp | undefined | null)[]
}

export const CustomLabel = styled(FormHelperText)(({ theme }) => ({
  flexShrink: 0,
  marginTop: 0,
  marginBottom: theme.spacing(0.75),
  fontSize: '14px',
  lineHeight: 1.43,
  fontWeight: 500,
  color: theme.palette.grey[700],

  '&.error': {
    marginTop: theme.spacing(0.5),
    marginLeft: '4px',
  },
}))

export const CustomInput = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '&.Mui-error': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error[600],
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#b1c7a0',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: '1px solid #a0bbbd',
        boxShadow: '0 0 0 4px #dde6c1',
      },
    },
  },
  '& input': {
    color: theme.palette.grey[900],
  },
  '& .startAdornment': {
    height: '40px',
    paddingLeft: theme.spacing(0.5),
  },

  '& .currency': {
    '& input': {
      paddingLeft: '49px',
    },
  },

  '& .inputField': {
    borderRadius: '8px',

    '&:hover': {
      '& fieldset': {
        borderColor: theme.palette.secondary[300],
      },
    },

    '&.Mui-disabled': {
      color: theme.palette.grey[500],
      backgroundColor: theme.palette.grey[50],
      borderColor: theme.palette.grey[200],
      '&:hover': {
        '& fieldset': {
          borderColor: theme.palette.grey[200],
        },
      },
    },

    '&:focus-within': {
      '& fieldset': {
        border: `1px solid ${theme.palette.primary[300]}`,
        boxShadow: `0 0 0 4px ${theme.palette.secondary[100]}`,
      },
    },

    '& input': {
      boxSizing: 'border-box',
      height: '44px',
      padding: theme.spacing(1.25, 1.5, 1.25, 1.5),
      fontSize: '15px',
      lineHeight: 1.5,
      borderRadius: '8px',

      '&::placeholder': {
        color: theme.palette.grey[500],
        opacity: 1,
      },
    },
  },

  '& .textareaField': {
    borderRadius: '8px',

    '&:hover': {
      '& fieldset': {
        borderColor: theme.palette.secondary[300],
      },
    },

    '&:focus-within': {
      '& fieldset': {
        border: `1px solid ${theme.palette.primary[300]}`,
        boxShadow: `0 0 0 4px ${theme.palette.secondary[100]}`,
      },
    },

    '& textarea': {
      boxSizing: 'border-box',
      paddingY: theme.spacing(1.25),
      paddingX: theme.spacing(1.5),
      fontSize: '15px',
      lineHeight: 1.5,
      borderRadius: '8px',

      '&::placeholder': {
        color: '#737373',
        opacity: 1,
      },
    },
  },
}))

export interface IProps extends OutlinedTextFieldProps {
  label?: string
  required?: boolean
  multiline?: boolean
  textError?: string
  mb?: string
  currency?: boolean
  width?: string
  validations?: IValidation[]
  value: string | number | null
  onIsValidChange?(name: string, isValid: boolean): void
  Icon?: any
  sx?: SxProps
  labelSmall?: boolean
  maxLength?: number
  labelIcon?: React.ReactNode
  min?: number
  max?: number
  step?: number
  disabled?: boolean
}
const calculateEncodedLength = (str: string | number) => {
  const encodedStr = he.encode(str, {
    useNamedReferences: true,
  })

  return encodedStr.length
}

// eslint-disable-next-line import/prefer-default-export
export function InputField(props: IProps) {
  const {
    label,
    error,
    required,
    textError,
    InputProps,
    defaultValue,
    mb,
    currency,
    width,
    validations,
    onChange,
    value,
    onIsValidChange,
    name,
    Icon,
    sx,
    labelSmall,
    maxLength,
    labelIcon,
    min,
    max,
    step,
    disabled,
    helperText,
    ...rest
  } = props
  const [inputError, setInputError] = useState<string>('')
  const [isTouched, setIsTouched] = useState<boolean>(false)

  const [validationSchema, setValidationSchema] = useState<any>()

  const validate = (value: string | number | undefined | null, validationSchema: any) => {
    if (validationSchema && name) {
      try {
        validationSchema.validateSync({ [name]: value })

        setInputError('')
        return true
      } catch (e: any) {
        setInputError(e.errors[0])

        return false
      }
    }
    return true
  }

  useEffect(() => {
    if (validations) {
      const validationSchema = createYupSchema(validations, name)

      setValidationSchema(validationSchema)
    }
  }, [validations])

  const onInputChange = (ev: any) => {
    if (onChange) {
      onChange(ev)
    }
  }

  const onBlur = (ev: any) => {
    setIsTouched(true)
    if (props.onBlur) {
      props.onBlur(ev)
    }
  }

  useEffect(() => {
    if (validationSchema) {
      validate(value, validationSchema)
    }
    if (value && !isTouched) {
      setIsTouched(true)
    }
  }, [value, validationSchema])

  useEffect(() => {
    if (onIsValidChange && name) {
      onIsValidChange(name, !inputError)
    }
  }, [inputError])
  const hasInputError = isTouched && inputError
  const decodedValue = value ? he.decode(value.toString()) : ''
  const encodedLength = calculateEncodedLength(decodedValue || '')

  return (
    <Box display="flex" flexDirection="column" width="100%" mb={mb || 2} sx={{ ...sx }} bgcolor="inherit">
      {label && (
        <CustomLabel
          required={required}
          sx={{
            display: 'flex',
            alignItems: 'center',
            fontSize: labelSmall ? '12.8px' : '14px',
            lineHeight: labelSmall ? 1.56 : 1.43,
            letterSpacing: labelSmall ? '-0.13px' : 0,
          }}
        >
          {label}
          {required && <span style={{ verticalAlign: 'super' }}>*</span>}
          {labelIcon && labelIcon}
        </CustomLabel>
      )}

      <CustomInput
        {...rest}
        disabled={disabled}
        value={decodedValue}
        name={name}
        onBlur={onBlur}
        onChange={onInputChange}
        defaultValue={defaultValue}
        error={!!error || !!hasInputError}
        label=""
        InputLabelProps={{ shrink: false }}
        helperText={!!error || !!hasInputError ? '' : helperText}
        InputProps={{
          startAdornment: Icon ? (
            <InputAdornment position="start">
              <Icon />
            </InputAdornment>
          ) : (
            ''
          ),
          classes: {
            root: props.multiline ? 'textareaField' : 'inputField',
          },
          ...InputProps,
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
          min,
          max,
          step,
        }}
        style={{ width: width || '', backgroundColor: '#fff' }}
        sx={{
          ...sx,
          '& input': { paddingLeft: currency ? '49px' : '' },
          '& .MuiFormHelperText-root': {
            marginLeft: 0,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: '1px solid',
              borderColor: 'grey.200',
            },
          },
        }}
      />
      {props.multiline && maxLength && value && maxLength - encodedLength <= 50 && maxLength - encodedLength >= 0 && (
        <Typography variant="caption" color="grey.500" ml="auto" mt={0.75}>
          {`${maxLength - (value ? encodedLength : 0)} character(s) left`}
        </Typography>
      )}
      {props.multiline && maxLength && value && encodedLength > maxLength && (
        <Typography variant="caption" color="error.600" ml="auto" mt={0.75}>
          Your entry exceeds a limit of {maxLength} characters
        </Typography>
      )}
      {error && (
        <CustomLabel error className="error">
          {textError}
        </CustomLabel>
      )}

      {!!hasInputError && (
        <CustomLabel error className="error">
          {inputError}
        </CustomLabel>
      )}
    </Box>
  )
}
